import { useCallback, useEffect } from 'react';
import { Linking } from 'react-native';
import { UnifiedDeepLinkData } from 'react-native-appsflyer';

import { useLinkTo, useNavigation } from '@react-navigation/native';

import { useLinking, useLinkingStore } from '@/feature/entry-share/hooks/use-linking';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { logger } from '@/utils/logging';
import isEqual from 'lodash/isEqual';

export const extractDeepLinkValue = (url?: string) => {
    const match = url?.match(/deep_link_value=([^&]+)/);
    return match ? match[1] : null;
};

export const isOneLink = (url: string) => {
    return url.includes('onelink.me');
};

const LOG_TAG = '[OneLink-AppsFlyer]';

// Tracks the deferred deep link already routed, so navigation fires once per resolved payload
// even though this hook is mounted in several product tab navigators.
let handledDeferredKey: string | undefined;
let lastHandledCasinoOneLink: UnifiedDeepLinkData | undefined;

/**
 * Custom hook for handling one-link navigation.
 * This hook handles deep link navigation based on the provided URL.
 * It uses the `useNavigation` hook from React Navigation to navigate to different screens.
 *
 * @returns An object containing the initial link and the current link.
 */
export const useOneLinkNavigation = () => {
    const navigation = useNavigation();
    const linkTo = useLinkTo();
    const oneLinkData = useLinkingStore(state => state.oneLinkData);
    const hasSocialWallet = useHasSocialWallet();
    const payByBankUrlIdentifier = 'hellopaybybank';

    const routeByDeepLinkValue = useCallback(
        (deepLinkValue: string | null) => {
            // Handle the deep link navigation
            if (deepLinkValue === 'deposit') {
                navigation.navigate(
                    'DepositStack',
                    hasSocialWallet ? { screen: 'CoinPackageScreen' } : { screen: 'DepositScreen', params: {} }
                );
            } else if (deepLinkValue === 'gaming_limits') {
                navigation.navigate('EntryScreen', { id: '' });
            } else if (deepLinkValue === 'event_sport') {
                navigation.navigate('FantasyPickSlipStack', { screen: 'FantasyPickSlip', params: {} });
            } else {
                logger.info(LOG_TAG, `Deep link value not handled: ${deepLinkValue}`);
            }
        },
        [navigation, hasSocialWallet]
    );

    const handleRedirectLink = useCallback(
        (url?: string) => {
            // Process PayByBank URL in an external link as requested by Paysafe and Certegy.
            // Placing this logic above ensures PayByBank URLs are processed first.
            if (url?.includes(payByBankUrlIdentifier)) {
                const bankAuthGatewayUrl = url.split('?url=')[1];
                if (bankAuthGatewayUrl) {
                    Linking.openURL(bankAuthGatewayUrl);
                }
            }

            if (!!url && !isOneLink(url)) {
                return;
            }

            logger.info(LOG_TAG, `Handling redirect link: ${url}`);
            routeByDeepLinkValue(extractDeepLinkValue(url));
        },
        [routeByDeepLinkValue]
    );

    const handleInitialLink = useCallback(
        (url: string) => {
            logger.info(LOG_TAG, `Initial link ${url}`);
            handleRedirectLink(url);
        },
        [handleRedirectLink]
    );

    const handleNewLink = useCallback(
        (url: string) => {
            logger.info(LOG_TAG, `New link ${url}`);
            handleRedirectLink(url);
        },
        [handleRedirectLink]
    );

    // Deferred deep links (DDL / deferred UDL) are resolved by the AppsFlyer SDK without an OS
    // URL, so the URL handlers above never see them. Route from the stored payload instead.
    // Gated on isDeferred so direct clicks keep flowing through the URL path (no double navigation).
    // Casino game and free spins links are handled by the dedicated effects below.
    useEffect(() => {
        if (!oneLinkData?.isDeferred) {
            return;
        }
        const deepLinkValue = oneLinkData.data?.deep_link_value;
        if (!deepLinkValue || deepLinkValue === 'casino_game' || deepLinkValue === 'free_spins') {
            return;
        }
        const key = `${oneLinkData.data?.link ?? ''}|${deepLinkValue}`;
        if (handledDeferredKey === key) {
            return;
        }
        handledDeferredKey = key;
        logger.info(LOG_TAG, `Deferred deep link value: ${deepLinkValue}`);
        routeByDeepLinkValue(deepLinkValue);
    }, [oneLinkData, routeByDeepLinkValue]);

    // Handle Casino game OneLinks from resolved AppsFlyer payload to be able to get the gameid from short links
    useEffect(() => {
        const data = oneLinkData?.data as Record<string, string | undefined> | undefined;
        const deepLinkValue = data?.deep_link_value;
        if (deepLinkValue !== 'casino_game' && deepLinkValue !== 'free_spins') {
            return;
        }
        if (isEqual(lastHandledCasinoOneLink, oneLinkData)) {
            return;
        }
        lastHandledCasinoOneLink = oneLinkData;

        if (deepLinkValue === 'casino_game') {
            const gameId = data?.game_id;
            logger.info(LOG_TAG, `Casino game deep link, gameId: ${gameId ?? 'none'}`);
            linkTo(gameId ? `/casino/game/${encodeURIComponent(gameId)}` : '/casino/game');
        } else {
            logger.info(LOG_TAG, 'Free spins deep link');
            linkTo('/casino/free-spins');
        }
    }, [oneLinkData, linkTo]);

    useLinking({
        source: 'OneLink-AppsFlyer',
        onInitialLink: handleInitialLink,
        onNewLink: handleNewLink,
    });
};
